import { ProgramConfig, ProgramEngagement, ProgramEngagementUserModel, UserId } from '@cibo/core'
import { Loading, useAuth, useOrgUser } from '@cibo/profile'
import { useDeleteProgramEngagement, useProgramEngagements } from '@cibo/programs'
import Delete from '@mui/icons-material/Delete'
import { LoadingButton } from '@mui/lab'
import { Button, Container, DialogActions, DialogContent, Stack, Typography } from '@mui/material'
import {
  DataGridProProps,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataGridPro, Markdown, ResponsiveDialog, Section } from '../../consts/Theme'

type ProgramEngagementsListProps = {
  programId: ProgramConfig['programId']
  userId: UserId
} & Pick<DataGridProProps<ProgramEngagement>, 'onRowClick'>

export const ProgramEngagementsList = ({
  onRowClick,
  programId,
  userId,
}: ProgramEngagementsListProps) => {
  const { t } = useTranslation('@cibo/impact/ProgramEngagementsList')
  const self = useAuth()
  const user = useOrgUser(userId)
  const programEngagements = useProgramEngagements({
    filters: [{ userId: [userId], programId: [programId] }],
  })
  const deleteEngagement = useDeleteProgramEngagement()
  const { enqueueSnackbar } = useSnackbar()
  const [confirmDeleteEngagement, setConfirmDeleteEngagement] = useState<ProgramEngagement>()

  const onDeleteEngagement = useCallback(() => {
    confirmDeleteEngagement &&
      deleteEngagement.mutateAsync(confirmDeleteEngagement).then(() => {
        setConfirmDeleteEngagement(undefined)
        enqueueSnackbar(t('deleteSuccess'), { variant: 'success' })
      })
  }, [confirmDeleteEngagement?.id])

  const columns = useMemo(
    () =>
      [
        {
          field: 'currentStepType',
          headerName: t('stepType'),
          minWidth: 80,
          renderCell: ({ value }: GridRenderCellParams<ProgramEngagement>) =>
            t(`stepType_${value}`),
        },
        {
          field: 'fields',
          headerName: t('fields'),
          flex: 1,
          valueGetter: params => params.row.fieldCounts?.total,
          renderCell: ({ value }: GridRenderCellParams<number, ProgramEngagement>) =>
            t('fields', { count: value }),
        },
        {
          field: 'updatedAt',
          headerName: t('updatedAt'),
          minWidth: 150,
          renderCell: (params: GridRenderCellParams<string, ProgramEngagement>) => {
            const user = new ProgramEngagementUserModel(params.row.updatedBy)

            return !!params.value ? (
              <Stack>
                <span>{dayjs(params.value).fromNow()}</span>
                {self.userId !== user.userId && (
                  <Typography variant="caption">{user.displayNameFull}</Typography>
                )}
              </Stack>
            ) : (
              '-'
            )
          },
        },
        {
          field: 'actions',
          type: 'actions',
          width: 80,
          getActions: (engagement: ProgramEngagement) => [
            //@ts-ignore GridActionsCellItem is expecting more props than are being passed, but it works.
            <GridActionsCellItem
              icon={<Delete />}
              label={t('delete')}
              className="textPrimary"
              onClick={() => setConfirmDeleteEngagement(engagement)}
              color="inherit"
            />,
          ],
        },
      ] as GridColDef<ProgramEngagement>[],
    []
  )

  if (programEngagements.isLoading || user.isLoading) {
    return <Loading />
  }

  return (
    <Container disableGutters>
      <Stack spacing={3}>
        <Typography variant="h6">
          {t('title', {
            name: user.data?.displayNameFull,
            count: programEngagements.data?.items?.length,
          })}
        </Typography>
        <Section>
          <DataGridPro
            columns={columns}
            rows={programEngagements.data?.items ?? []}
            onRowClick={onRowClick}
            hideCellFocus
            hideFooter
            disableSelectionOnClick
            autoHeight
          />
        </Section>
      </Stack>
      <ResponsiveDialog
        open={!!confirmDeleteEngagement}
        onClose={() => setConfirmDeleteEngagement(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Markdown>{t('deleteDialogMessage')}</Markdown>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteEngagement(undefined)}>{t('cancel')}</Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={onDeleteEngagement}
            loading={deleteEngagement.isLoading}
          >
            {t('delete')}
          </LoadingButton>
        </DialogActions>
      </ResponsiveDialog>
    </Container>
  )
}
